import { Filter } from '../entities/Filter';
import { FilterSet } from '../entities/FilterSet';
import { FilterQueryLogic, IFilterSets } from '../../views/product-filters/IProductFilters';
import { create } from './FilterSetFactory';
import { Sku } from '~domain/entities/Sku';
import { Product } from '~domain/entities/Product';

export enum filterLogic {
    AND = 'AND',
    OR = 'OR'
}

const enum ArrayConditions {
    'EVERY' = 'every',
    'SOME' = 'some'
}

export class FilterService {
    public filter<Type>(
        entities: Type[],
        filterSets: FilterSet[],
        ignoredFilterSets: FilterSet[] = []
    ): Type[] {
        const orFilterSets = filterSets.filter(
            (filterSet) =>
                filterSet.logic === FilterQueryLogic.OR &&
                !this.isIgnored(filterSet, ignoredFilterSets)
        );
        const andFilterSets = filterSets.filter(
            (filterSet) =>
                filterSet.logic === FilterQueryLogic.AND &&
                !this.isIgnored(filterSet, ignoredFilterSets)
        );

        // if there are no sku filters don't filter the skus
        const isFilteringSkus = this.isFilteringSkus(entities);
        const filterAtSkuLevel = this.hasSkuFilters(filterSets);

        if (
            (!isFilteringSkus && !andFilterSets.length && !orFilterSets.length) ||
            (isFilteringSkus && !filterAtSkuLevel)
        ) {
            return entities;
        }

        const filteredEntities = entities.filter((entity: Type) => {
            return (
                this.someFiltersArePassing(orFilterSets, entity) &&
                this.allFiltersArePassing(andFilterSets, entity)
            );
        });

        return filteredEntities;
    }

    private hasSkuFilters(filterSets: FilterSet[]) {
        return filterSets.some((filterSet: FilterSet) => filterSet.hasSkuFilters);
    }

    private isFilteringSkus<Type>(entities: Type[]) {
        return entities?.length && entities[0] instanceof Sku;
    }

    private isIgnored(filterSet: FilterSet, ignoredFilterSets: FilterSet[]) {
        return ignoredFilterSets.some((_filterSet) => _filterSet.key === filterSet.key);
    }

    public getAppliedFilters(appliedFiltersSet: FilterSet[]): Filter[] {
        return appliedFiltersSet.reduce((appliedFilters: Filter[], filterSet: FilterSet) => {
            return appliedFilters.concat(filterSet.activeFilters);
        }, []);
    }

    // create a Query String to reflect the applied filter state
    // while maintaining any other url parameter that is unrelated to filters
    public getAppliedFiltersQueryString(
        appliedFiltersSet: FilterSet[],
        allFiltersSet: FilterSet[],
        currentUrlParams: string
    ): string {
        let newUrl = '';

        // construct a new query string from the applied filters
        // add all existing so we don't lose needed params such as utm_*
        const urlParams = new URLSearchParams(currentUrlParams);

        // clear any existing keys that are filters, as we will be re-applying them
        allFiltersSet.forEach((filter) => urlParams.delete(this.convertFilterKeyLabel(filter.key)));

        // iterate over the applied filter sets
        appliedFiltersSet.forEach((filterSet) => {
            // find the active filters from the set and extract their keys
            const activeFilterKeys = filterSet.activeFilters.map((filter) => {
                return this.convertFilterValueLabel(filter.label);
            });
            // set one query string param per filter set
            urlParams.set(this.convertFilterKeyLabel(filterSet.key), activeFilterKeys.join(','));
        });
        // reconstruct URL
        const newParamStr = urlParams.toString();
        const separator = newParamStr.length > 0 ? '?' : '';
        try {
            newUrl = `${window.location.pathname}${separator}${decodeURIComponent(newParamStr)}`;
        } catch (error) {
            console.error(error);
            newUrl = window.location.pathname;
        }
        return newUrl;
    }

    // convert label for filter value into query param format
    // e.g. "Dry Skin" -> "dry-skin"
    public convertFilterValueLabel = (label: string): string => {
        return label.toLowerCase().trim().replace(/ /g, '-').replace(/&/g, '%26');
    };

    // convert label for filter key into query param format
    // e.g. "skin_concern" -> "skin-concern"
    public convertFilterKeyLabel = (label: string): string => {
        return label.replace(/_/g, '-');
    };

    public static getAnalyticsAppliedFilters(appliedFilters: Filter[]) {
        return appliedFilters.map(Filter.toJson);
    }

    private checkFiltersUsingMethod<Type>(
        filterSets: FilterSet[],
        entity: Type,
        method: ArrayConditions
    ) {
        if (!filterSets.length) {
            return true;
        }

        return filterSets[method]((filterSet) => filterSet.passes(entity));
    }

    private someFiltersArePassing<Type>(filterSets: FilterSet[], product: Type) {
        return this.checkFiltersUsingMethod(filterSets, product, ArrayConditions.SOME);
    }

    private allFiltersArePassing<Type>(filterSets: FilterSet[], product: Type) {
        return this.checkFiltersUsingMethod(filterSets, product, ArrayConditions.EVERY);
    }

    public getApplicableFilters = <Type>(filtersData: FilterSet[], entities: Type[]) => {
        filtersData.forEach((filterSet) => {
            const applicableEntities =
                filterSet.logic === FilterQueryLogic.OR
                    ? entities
                    : this.filter(entities, filtersData, [filterSet]);

            if (entities[0] instanceof Product) {
                this.simulateFilters((<unknown>applicableEntities) as Product[], filtersData, [
                    filterSet
                ]);
            }

            filterSet.filters.forEach((filterItem) => {
                const filterAppliesToEntities = this.filterAppliesToEntities(
                    filterItem,
                    filterSet,
                    applicableEntities,
                    undefined,
                    true
                );
                filterItem.setDisabledState(!filterAppliesToEntities);
                if (filterItem.active && !filterAppliesToEntities) {
                    filterItem.setActiveState(false);
                }
            });
        });

        return filtersData;
    };

    public simulateFilters = (
        applicableEntities: Product[],
        filtersData: FilterSet[],
        ignoredFiltesData: FilterSet[] = []
    ) => {
        applicableEntities.forEach((product: Product) => {
            const skuLevelFilters = filtersData.filter((filterSet) => filterSet.hasSkuFilters);

            const filteredSkus = this.filter(
                product.initialSkus,
                skuLevelFilters,
                ignoredFiltesData
            );

            if (filteredSkus && filteredSkus.length > 0) {
                product.simulatedSkus = filteredSkus;
            }
        }, []);
    };

    public generateFilterSets = <Type>(
        filterSetsJson: IFilterSets[],
        entities: Type[],
        applicableEntities?: Type[]
    ) => {
        const selectedFilters = create(filterSetsJson);

        return selectedFilters.reduce(
            (activeFiltersSet: FilterSet[], currentFilterSet: FilterSet): FilterSet[] => {
                const activeFilters = currentFilterSet.filters.reduce(
                    (_activeFilters: Filter[], currentFilter: Filter): Filter[] => {
                        return this.getActiveFiltersForSet(
                            _activeFilters,
                            currentFilter,
                            currentFilterSet,
                            entities,
                            applicableEntities
                        );
                    },
                    []
                );

                if (activeFilters.length) {
                    currentFilterSet.setApplicableFilters(activeFilters);
                    activeFiltersSet.push(currentFilterSet);
                }

                return activeFiltersSet;
            },
            []
        );
    };

    private getActiveFiltersForSet<Type>(
        _activeFilters: Filter[],
        currentFilter: Filter,
        currentFilterSet: FilterSet,
        entities: Type[],
        applicableEntities?: Type[]
    ): Filter[] {
        if (
            this.filterAppliesToEntities(
                currentFilter,
                currentFilterSet,
                entities,
                applicableEntities
            )
        ) {
            _activeFilters.push(currentFilter);
        }

        return _activeFilters;
    }

    private filterAppliesToEntities<Type>(
        currentFilter: Filter,
        currentFilterSet: FilterSet,
        entities: Type[],
        applicableEntities?: Type[],
        useSimulated: boolean = false
    ) {
        const checkEntities = applicableEntities || entities;

        return checkEntities.some((entity: Type) => {
            return currentFilterSet.filterQueries.some((filterSet) =>
                currentFilter.passes(filterSet.extract(entity, useSimulated))
            );
        });
    }

    public getAppliedFilterSets(filtersData: FilterSet[]) {
        return filtersData.filter((filterSet) => filterSet.activeFilters.length);
    }
}
